'use client';

import type {
  CustomerOrder,
  CustomOrderItem,
  VirtualProduct,
} from '@eliadmin/core/src/api/customers/customers-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import {
  downloadVirtualProduct,
  fetchCustomerOrders,
} from '@eliadmin/core/src/api/customers/customers-api';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { LuDownload } from 'react-icons/lu';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';

export default function MyProductsPage() {
  const locale = useLocaleStore(s => s.currentLocale);
  const me = useCustomerStore(s => s.me);
  const authToken = me!.authToken;

  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const page = searchParams.get('page') || '1';

  const { data: orders, isLoading } = useQuery({
    queryKey: [CacheKey.ORDERS, { page }],
    queryFn: () => fetchCustomerOrders({ locale, authToken, query: { page: Number(page) } }),
  });

  if (isLoading) return <GenericLoader />;

  if (!orders?.data?.length) {
    return <p className="text-bold text-sm text-muted-foreground">{t('text445')}</p>;
  }

  const itemsWithOrder = orders.data.flatMap(order =>
    order.items.filter(item => item.virtual_product != null).map(item => ({ order, item })),
  );

  if (!itemsWithOrder.length) {
    return (
      <p className="text-bold text-sm text-muted-foreground">
        Nemáte žiadne produkty na stiahnutie.
      </p>
    );
  }

  return (
    <div className="flex w-full flex-col items-start justify-start gap-4">
      <h4 className="mb-4 text-xl font-bold text-foreground sm:mb-6 sm:text-2xl lg:mb-8">
        {t('text3125')}
      </h4>

      <div className="flex w-full flex-col items-start justify-start gap-4">
        <div className="hidden w-full flex-row items-center gap-4 p-4 md:flex">
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            Produkt
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            Kategória
          </span>
          <span className="flex-1 text-end text-sm font-semibold text-muted-foreground">
            Stiahnuť
          </span>
        </div>
        {/* content */}
        {itemsWithOrder.map(({ order, item }) => (
          <MyProductCard key={`${order.id}-${item.id}`} product={item} order={order} />
        ))}
      </div>
    </div>
  );
}

function MyProductCard({ product, order }: { product: CustomOrderItem; order: CustomerOrder }) {
  const locale = useLocaleStore(s => s.currentLocale);
  const me = useCustomerStore(s => s.me);
  const isPaid = order.paid;

  const { t } = useTranslations();

  function getFilenameFromContentDisposition(contentDisposition: any) {
    if (!contentDisposition) return null;
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    return matches?.[1]?.replace(/['"]/g, '') || null;
  }

  const handleDownloadVirtual = (virtual_product?: VirtualProduct) => {
    if (!virtual_product || !me?.authToken) return;

    downloadVirtualProduct({
      authToken: me?.authToken,
      locale,
      productId: virtual_product.id,
    })
      .then(async response => {
        if (!response.ok) {
          toast({ title: t('text1695'), variant: 'destructive' });
          return;
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = getFilenameFromContentDisposition(contentDisposition) || 'download.zip';

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast({ title: t('text1695'), variant: 'destructive' });
      });
  };

  const hasVirtualProduct = !!product.virtual_product?.file;

  console.log(product);

  return (
    <Card className="w-full rounded-xl border border-border">
      <CardContent className="hidden flex-row items-center gap-4 p-4 sm:flex">
        <div className="flex flex-1 flex-col items-start justify-start gap-2">
          <span className="flex-1 text-start text-sm font-bold">{product.name}</span>
          {product.virtual_product?.password && (
            <p className="text-sm">
              Heslo: {isPaid ? product.virtual_product.password : 'sa zobrazí po zaplatení'}
            </p>
          )}
        </div>
        <span className="flex-1 text-start text-sm">
          {product.product.categories[0]?.name} {order.order_created_at}
        </span>
        <div className="flex flex-1 items-center justify-end">
          {isPaid && hasVirtualProduct && (
            <Button
              onClick={() => handleDownloadVirtual(product.virtual_product)}
              className="flex w-full items-center justify-center gap-2 sm:w-fit"
            >
              <LuDownload size={20} /> Stiahnuť
            </Button>
          )}
          {!isPaid && (
            <p className="text-start text-sm text-red-600">Objednávka nebola zaplatená</p>
          )}
        </div>
      </CardContent>
      <CardContent className="flex flex-col items-start justify-start gap-8 pt-4 sm:hidden">
        <div className="flex w-full items-center justify-start gap-4">
          <div className="flex w-full flex-col items-start justify-start gap-2">
            <span className="flex-1 text-start text-sm font-bold">{product.name}</span>
            {product.virtual_product?.password && (
              <p className="text-sm">
                Heslo: {isPaid ? product.virtual_product.password : 'sa zobrazí po zaplatení'}
              </p>
            )}
          </div>
          <span className="w-fit whitespace-nowrap text-end text-sm">
            {product.product.categories[0]?.name}
          </span>
        </div>

        <div className="flex w-full items-center justify-center">
          {isPaid && hasVirtualProduct && (
            <Button
              onClick={() => handleDownloadVirtual(product.virtual_product)}
              className="flex w-full items-center justify-center gap-2 sm:w-fit"
            >
              <LuDownload size={20} /> Stiahnuť
            </Button>
          )}
          {!isPaid && (
            <p className="text-start text-sm text-red-600">Objednávka nebola zaplatená</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
